import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UploadsService } from '../uploads.service';
import { shareInflightRequest } from '../../utils/share-inflight-request.util';
import { IDrawingsService } from '../../interfaces/services/drawings-service.interface';
import { Drawing } from '../../interfaces/state/entities/drawing.interface';
import { environment } from '../../environments/environment';
import {
  completeBlobUploadAfterPut,
  putBlobWithProgress,
  resolveBlobPutUrl,
} from '../../utils/blob-upload.utils';

@Injectable({
  providedIn: 'root',
})
export class DrawingsService implements IDrawingsService {
  private http = inject(HttpClient);
  private uploads = inject(UploadsService);
  private apiUrl = environment.MayaTestToolApiBaseUrl;
  private readonly inflightDrawings = new Map<string, Observable<Drawing[]>>();

  private readonly revisionNamePattern = /^revision\s+(\d+)$/i;

  getDrawings(floorId: string): Observable<Drawing[]> {
    return shareInflightRequest(this.inflightDrawings, floorId, () => {
      const params = new HttpParams().set('floorId', floorId);
      return this.http.get<{ drawings: any[] }>(`${this.apiUrl}/drawings`, { params }).pipe(
        map((response) =>
          (response.drawings ?? []).map((d) => {
            const processIdRaw = d.processId ?? d.ProcessId;
            const processId = processIdRaw != null ? String(processIdRaw).trim() : '';
            return {
              id: d.drawingId,
              floorId,
              drawingName: d.drawingName ?? '',
              status: d.status ?? '',
              processId: processId || undefined,
              dateCreated: new Date(d.createdAt),
              dateProcessed: d.dateProcessed ? new Date(d.dateProcessed) : undefined,
              lastModified: new Date(d.lastModified ?? d.createdAt),
              revisionNumber: d.revisionNumber ?? 0,
              fileSize: d.fileSize != null ? String(d.fileSize) : '0',
            };
          })
        )
      );
    });
  }

  addDrawing(floorId: string, name: string, fileSize: number, file: File): Observable<Drawing> {
    const now = new Date();
    const revisionMatch = this.revisionNamePattern.exec(name.trim());
    const revisionNumber = revisionMatch ? Number(revisionMatch[1]) : 0;

    return this.http
      .post<{ drawingId: string; uploadUrl: string }>(`${this.apiUrl}/drawings`, { floorId, drawingName: name })
      .pipe(
        switchMap(({ drawingId, uploadUrl }) =>
          this.uploadDrawingFile(drawingId, fileSize, file, uploadUrl).pipe(
            map(() => ({
              id: drawingId,
              floorId,
              drawingName: name,
              status: 'Pending',
              dateCreated: now,
              dateProcessed: undefined,
              lastModified: now,
              revisionNumber,
              fileSize: String(fileSize),
            }))
          )
        )
      );
  }

  retryProcessingDrawing(drawingId: string, fileSize: number): Observable<void> {
    return this.http
      .post(`${this.apiUrl}/processedDrawings`, { drawingId, fileSize })
      .pipe(map(() => undefined));
  }

  private uploadDrawingFile(
    drawingId: string,
    fileSize: number,
    file: File,
    uploadUrl: string,
    useProvidedUploadUrl = false
  ): Observable<void> {
    this.uploads.start(drawingId, file.name, 'drawing', file.size);
    return from(resolveBlobPutUrl(drawingId, file.name, uploadUrl, useProvidedUploadUrl)).pipe(
      switchMap((putUrl) =>
        putBlobWithProgress(this.http, putUrl, file, (loaded) => this.uploads.progress(drawingId, loaded))
      ),
      map(() => true),
      catchError(() => of(false)),
      switchMap((uploadSucceeded) =>
        completeBlobUploadAfterPut(this.http, this.apiUrl, uploadSucceeded, 'processedDrawings', {
          drawingId,
          fileSize,
        }).pipe(
          tap({
            next: () => this.uploads.finish(drawingId, uploadSucceeded),
            error: () => this.uploads.finish(drawingId, false),
          })
        )
      )
    );
  }
}
